import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import {
  Alert,
  Badge,
  Container,
  Typography,
  Grid,
  Row,
  Col,
  Button,
  Menu,
  MenuItem,
  Option,
  Select,
  Switch,
  Input,
  Toast,
  ToastTitle,
} from '@sm/wds-react';
import { IconStarFilled, IconMore, IconArrowLeft, IconArrowRight } from '@sm/wds-icons';
import MultipleChoice from '../../components/MultipleChoice';
import { sendMessage, getQuestionConfigs } from './helper';

import { CreateWizardConfigContext } from '../../CreateWizardConfigContext';

import './new-survey.scss';
import ACTION_TYPES, { determineActionType } from './actionTypes';

import {
  MULTI_CHOICE_QUESTIONS,
  SINGLE_TEXTBOX,
  COMMENT_BOX,
  STAR_RATING,
  SLIDER,
  getQuestionTypes,
} from './questionTypes';
import createNewQuestion, { isQuestionInvalid } from './questions';
import { mapQuestionFromApi } from './questionTransformer/questionFromApi';
import { buildQuestionData, mapQuestionToAPI } from './questionTransformer/questionToApi';
import { getInputConfig } from './inputConfig';

const NewSurvey = ({ match }) => {
  const { createWizardConfig, setCreateWizardConfig } = useContext(CreateWizardConfigContext);
  const {
    primaryCtaCopy,
    secondaryCtaCopy,
    style,
    survey,
    redirectToEditorPage,
    redirectTo,
    readOnlySurvey,
    showPrimaryCtaIcon,
    parentDomain,
    questionConfig,
  } = createWizardConfig;
  const surveyTypeSelected = redirectToEditorPage ? redirectTo : match.params.surveyTypeSelected;

  const defaultInputConfig = getInputConfig({ survey, surveyType: surveyTypeSelected, style });

  const {
    hideCreateSurveyButton,
    hideCancelSurveyButton,
    sendPayloadOnAllUpdates,
    maxQuestions: MAX_QUESTIONS,
    types,
  } = defaultInputConfig;

  const [inputData] = useState(defaultInputConfig);

  const [surveyDetails, setSurveyDetails] = useState(survey);

  const [questions, setQuestions] = useState([createNewQuestion()]);

  const [surveyTitle, setSurveyTitle] = useState(survey && survey.title);

  const [surveyType, setSurveyType] = useState(surveyTypeSelected);

  const [shouldRedirectBackToHome, setShouldRedirectBackToHome] = useState(false);

  const [redirectUrl, setRedirectUrl] = useState('/createwizard/');

  const isNewSurvey = Object.keys(survey).length === 0;

  const edit = redirectToEditorPage && !isNewSurvey;

  const [questionsFromApiMap, setQuestionsFromApiMap] = useState({});

  const [questionConfigs] = useState(getQuestionConfigs(questionConfig, surveyTypeSelected));

  const [showUnsupportedAlert, setUnsupportedAlert] = useState(false);

  const loadMappedQuestionsList = useCallback(
    surveyData => {
      const questionsList = [];
      const questionsFromAPIList = [];
      if (edit && surveyData) {
        surveyData.pages.forEach(page => {
          page.questions.forEach(question => {
            const q = mapQuestionFromApi(question, surveyDetails);
            if (q) {
              if (questionConfigs.disabledQuestionTypes.includes(q.questionType)) {
                q.unsupported = true;
                setUnsupportedAlert(true);
              }

              questionsList.push(q);
              questionsFromAPIList.push(question);
            }
          });
        });
        setQuestions(questionsList);
        setQuestionsFromApiMap(
          questionsFromAPIList.reduce((map, question) => {
            const newMap = map;
            if (question.id) {
              newMap[question.id] = question;
            }
            return newMap;
          }, {})
        );
      }
    },
    [edit, surveyDetails, questionConfigs]
  );

  useEffect(() => {
    loadMappedQuestionsList(survey);
    setSurveyDetails(survey);
  }, [edit, loadMappedQuestionsList, survey]);

  const {
    iconColor,
    buttonColor,
    buttonTextColor,
    secondaryButtonColor,
    secondaryButtonTextColor,
    badgeColor,
    badgeTextColor,
    containerBackgroundColor,
    middleContainerBackgroundColor = 'transparent',
    inputBackgroundColor,
    inputBorderColor,
    inputBorderHoverColor,
    inputBorderFocusColor,
    inputLabelBackgroundColor,
  } = style;

  useEffect(() => {
    const root = document.documentElement;

    root.style.setProperty('--customIconColor', iconColor);
    root.style.setProperty('--customButtonColor', buttonColor);
    root.style.setProperty('--customTextColor', buttonTextColor);
    root.style.setProperty('--customSecondaryButtonColor', secondaryButtonColor);
    root.style.setProperty('--customSecondaryTextColor', secondaryButtonTextColor);
    root.style.setProperty('--customBadgeColor', badgeColor);
    root.style.setProperty('--customBadgeTextColor', badgeTextColor);
    root.style.setProperty('--customContainerBackgroundColor', containerBackgroundColor);
    root.style.setProperty('--customMiddleContainerBackgroundColor', middleContainerBackgroundColor);
    root.style.setProperty('--customInputBackgroundColor', inputBackgroundColor);
    root.style.setProperty('--customInputBorderColor', inputBorderColor);
    root.style.setProperty('--customInputBorderHoverColor', inputBorderHoverColor);
    root.style.setProperty('--customInputBorderFocusColor', inputBorderFocusColor);
    root.style.setProperty('--customInputLabelBackgroundColor', inputLabelBackgroundColor || '#F3F2F1');
  }, [
    iconColor,
    buttonColor,
    buttonTextColor,
    secondaryButtonColor,
    secondaryButtonTextColor,
    badgeColor,
    badgeTextColor,
    containerBackgroundColor,
    middleContainerBackgroundColor,
    inputBackgroundColor,
    inputBorderColor,
    inputBorderHoverColor,
    inputBorderFocusColor,
    inputLabelBackgroundColor,
  ]);

  const showMore = false;

  const hasresponses = false;
  const [showToast, setShowToast] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [setDeletePromptQuestion] = useState(undefined);
  const [questionsToDelete, setQuestionsToDelete] = useState([]);

  const hasErrors = () => {
    if (!surveyTitle && surveyType !== 'poll') {
      return { focus: 'surveyTitle', message: 'Please enter survey title' };
    }

    for (let x = 0; x < questions.length; x += 1) {
      const question = questions[x];

      const questionErrors = isQuestionInvalid(question, surveyType, questionConfigs);
      if (questionErrors.length > 0) {
        return questionErrors[0];
      }
    }

    return null;
  };

  const saveSurvey = ({ overrideAction }) => {
    let dataToSend = {};

    const { questionsToSend } = buildQuestionData(questions, surveyType);

    dataToSend = {
      action: overrideAction || (readOnlySurvey ? 'read_only' : 'save'),
      changeIds: [],
      survey: {
        title: surveyTitle,
        pages: [
          {
            questions: questionsToSend,
          },
        ],
      },
      customConfigs: questionConfigs.config.customConfigs,
    };
    sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
  };

  const handleSurveyTypeChange = e => {
    setSurveyType(e.target.attributes.value.value);

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }
  };

  const errorClass = 'sm-new-survey__error-outline';

  const updateQuestion = _question => {
    const updatedQuestion = { ..._question, isUpdated: true };
    const questionsTemp = questions;
    const foundIndex = questionsTemp.findIndex(q => q.id === updatedQuestion.id);
    if (foundIndex > -1) {
      questionsTemp[foundIndex] = updatedQuestion;
      setQuestions([...questionsTemp]);
    }

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }

    const errOutline = document.querySelector('.sm-new-survey__input.sm-new-survey__error-outline');
    if (errOutline) {
      errOutline.classList.remove(errorClass);
    }
  };

  const handleQuestionTypeChange = (_question, e) => {
    const tmpQuestion = _question;
    const { value } = e.target.attributes.value;
    tmpQuestion.questionType = value;
    updateQuestion(tmpQuestion);
  };

  const handleQuestionChange = (_question, event) => {
    const tmpQuestion = _question;
    tmpQuestion.question = event.target.value;
    if (surveyType === 'poll') {
      setSurveyTitle(tmpQuestion.question);
    }
    updateQuestion(tmpQuestion);
  };

  const handleMinChange = (_question, e) => {
    const tmpQuestion = _question;

    tmpQuestion.min = `${e.target.value === '-' ? '-' : parseInt(e.target.value, 10) || ''}`;
    updateQuestion(tmpQuestion);
  };

  const handleMaxChange = (_question, e) => {
    const tmpQuestion = _question;

    tmpQuestion.max = `${e.target.value === '-' ? '-' : parseInt(e.target.value, 10) || ''}`;
    updateQuestion(tmpQuestion);
  };

  const handleRequiredChange = (_question, e) => {
    const tmpQuestion = _question;
    tmpQuestion.required = e.target.checked;
    updateQuestion(tmpQuestion);
  };

  const addQuestion = () => {
    setQuestions([...questions, createNewQuestion()]);
    const questionTitles = document.getElementsByClassName('sm-new-survey__question-title');
    questionTitles[questionTitles.length - 1].focus();
  };

  const deleteQuestion = question => {
    if (!question.isNew && hasresponses) {
      setDeletePromptQuestion(question);
    } else {
      const questionsToDeleteList = questionsToDelete;
      const filteredQuestions = questions.filter(q => q.id !== question.id);
      if (!question.isNew) {
        questionsToDeleteList.push(question);
      }
      setQuestions([...filteredQuestions]);
      setQuestionsToDelete([...questionsToDeleteList]);
    }
  };

  const handleSurveyTitleChange = e => {
    setSurveyTitle(e.target.value);

    if (sendPayloadOnAllUpdates) {
      saveSurvey();
    }

    const errOutline = document.querySelector('.sm-new-survey__input.sm-new-survey__error-outline');
    if (errOutline) {
      errOutline.classList.remove(errorClass);
    }
  };

  const moveQuestion = (question, down) => {
    const questionsList = questions;
    const from = questionsList.findIndex(q => q.id === question.id);
    const to = down ? from + 1 : from - 1;
    questionsList.splice(from, 1);
    questionsList.splice(to, 0, question);
    setQuestions([...questionsList]);
  };

  const showErrors = errObj => {
    if (errObj?.focus) {
      const input = document.getElementById(errObj.focus);
      input.focus();
      input.classList.add(errorClass);
    }
    setShowToast(true);
    setErrorText(errObj ? errObj.message : '');
  };

  const handleUpdate = ({ overrideAction }) => {
    const questionsToSend = [];
    const errors = hasErrors();
    if (errors) {
      showErrors(errors);
      return;
    }
    setShowToast(false);

    const changeIds = [];

    questions.forEach((question, position) => {
      const questionFromApi = questionsFromApiMap[question.id] || null;
      const mappedQuestion = mapQuestionToAPI(question, position + 1, surveyType === 'quiz', questionFromApi);

      if (question.initialPosition !== mappedQuestion.position || question.isUpdated || question.isNew) {
        changeIds.push(question.id);
        questionsToSend.push(mappedQuestion);
      }
    });

    questionsToDelete.forEach(questionToDelete => {
      changeIds.push(questionToDelete.id);
    });

    const action = determineActionType(readOnlySurvey, changeIds, edit);

    const dataToSend = {
      action: overrideAction || action,
      changeIds,
      survey: {
        id: surveyDetails.id,
        title: surveyTitle,
        pages: [
          {
            questions: questionsToSend,
          },
        ],
      },
      customConfigs: questionConfigs.config.customConfigs,
    };
    sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
  };

  const handleSave = ({ overrideAction }) => {
    const errors = hasErrors();
    if (errors) {
      showErrors(errors);
      return;
    }
    setShowToast(false);
    saveSurvey({ overrideAction });
  };

  const handleSubmit = ({ overrideAction = '' } = {}) => {
    if (questions.length === 0) {
      return;
    }

    localStorage.removeItem('createExperiencePayload');
    if (edit && surveyDetails.id) {
      handleUpdate({ overrideAction });
    } else {
      handleSave({ overrideAction });
    }
  };

  const handleCancel = actionType => {
    if (redirectTo === 'templates') {
      setRedirectUrl('/createwizard/templates');
      setShouldRedirectBackToHome(true);
    } else if (redirectToEditorPage || readOnlySurvey) {
      const dataToSend = {
        action: actionType,
      };
      sendMessage(`${JSON.stringify(dataToSend)}`, parentDomain);
      localStorage.removeItem('createExperiencePayload');
      setCreateWizardConfig({
        ...createWizardConfig,
        redirectTo: '',
        redirectToEditorPage: false,
        readOnlySurvey: false,
        survey: {},
      });
      setRedirectUrl('/createwizard');
      setShouldRedirectBackToHome(true);
    } else {
      setRedirectUrl('/createwizard');
      setShouldRedirectBackToHome(true);
    }
  };

  const onCloseToast = () => setShowToast(false);

  const isSubmitDisabled = () =>
    (questions && questions.length === 0) || (MAX_QUESTIONS && questions && questions.length > MAX_QUESTIONS);

  let primaryCtaIcon = '';
  if (showPrimaryCtaIcon) {
    primaryCtaIcon = <IconArrowRight />;
  }

  return (
    <Container data-testid="NewSurvey" className="">
      <Grid className="sm-new-survey" noBleeds>
        <Row className="sm-new-survey__top" mt={2}>
          <Col className="sm-new-survey__top-col">
            <If condition={shouldRedirectBackToHome}>
              <Redirect to={redirectUrl} />
            </If>
            <Container mt={2} ml={4}>
              <Button
                className="sm-new-survey__back-button"
                size="sm"
                variant="text"
                onClick={() => handleCancel(ACTION_TYPES.BACK)}
              >
                <IconArrowLeft />
                Back
              </Button>
            </Container>
            <Container pl={4}>
              <Typography variant="cardTitle">
                Create&nbsp;
                {surveyTypeSelected}
              </Typography>
            </Container>
          </Col>
        </Row>
        <Row className="sm-new-survey__middle">
          <Col className="sm-new-survey__middle-container">
            <Alert color="info" showIcon show={showUnsupportedAlert} onClose={() => setUnsupportedAlert(false)}>
              This survey contains question types that are not supported with your current SurveyMonkey plan.{' '}
              <span className="sm-new-survey__mobile">
                Upgrade your plan or update the paid question types to use this template.
              </span>
              <span className="sm-new-survey__desktop">
                <a href="https://www.surveymonkey.com/pricing/individual/details/" target="_blank" rel="noreferrer">
                  Upgrade
                </a>
                &nbsp; your plan or update the paid question types to use this template.&nbsp;
                <a
                  href="https://help.surveymonkey.com/en/surveymonkey/billing/surveymonkey-plans/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn More
                </a>
              </span>
              &nbsp;
            </Alert>
            <If condition={inputData.showBadge}>
              <Container className="sm-new-survey__survey-type" my={5}>
                {types && types.length >= 1 && (
                  <If condition={MAX_QUESTIONS === 1}>
                    <Badge size="lg">Poll</Badge>
                    <Else />
                    <Badge size="lg">
                      <Select
                        stretched
                        size="sm"
                        className="sm-new-survey__survey-type sm-new-survey__survey-type-selection"
                        value={surveyType}
                        onChange={handleSurveyTypeChange}
                        disabled={edit || readOnlySurvey}
                      >
                        {types.map(supportedSurveyType => {
                          return (
                            <Option key={uuid()} value={supportedSurveyType.toLowerCase()}>
                              {supportedSurveyType}
                            </Option>
                          );
                        })}
                      </Select>
                    </Badge>
                  </If>
                )}
              </Container>
            </If>
            {MAX_QUESTIONS !== 1 && (
              <Container mb={5}>
                <Input
                  id="surveyTitle"
                  className="surveyTitle"
                  stretched
                  size="sm"
                  label="Title"
                  value={surveyTitle || ''}
                  placeholder="Enter survey title"
                  onChange={handleSurveyTitleChange}
                  disabled={readOnlySurvey}
                />
              </Container>
            )}
            {questions?.map((question, index) => {
              const questionID = `question-${question.id}`;
              return (
                <Container
                  key={`question_container_${question.id}_${index + 1}`}
                  mb={questions && questions.length - 1 === index ? 9 : 0}
                >
                  {MAX_QUESTIONS !== 1 && !readOnlySurvey && (
                    <>
                      <hr />
                      <Container className="sm-new-survey__question-title" mb={3}>
                        <Typography component="div" variant="cardTitle">
                          <span className="sm-new-survey__title">Q {index + 1}</span>
                          <Menu
                            show={showMore}
                            className="questionMenu"
                            placement="bottom-right"
                            noRules
                            dense
                            menuTrigger={
                              <Button className="sm-new-survey__more-menu" size="sm" variant="text">
                                <IconMore />
                              </Button>
                            }
                          >
                            <MenuItem onClick={() => deleteQuestion(question)}>Delete question</MenuItem>
                            {index !== 0 && (
                              <MenuItem onClick={() => moveQuestion(question, false)}>Move question up</MenuItem>
                            )}
                            {questions && questions.length - 1 !== index && (
                              <MenuItem onClick={() => moveQuestion(question, true)}>Move question down</MenuItem>
                            )}
                          </Menu>
                        </Typography>
                      </Container>
                    </>
                  )}
                  <Container mb={5}>
                    <Select
                      stretched
                      size="sm"
                      className="sm-new-survey__question-type"
                      value={question.questionType}
                      onChange={event => handleQuestionTypeChange(question, event)}
                      disabled={(edit && !question.isNew) || readOnlySurvey}
                    >
                      {getQuestionTypes(questionConfigs.enabledQuestionTypes).map(questionType => {
                        return (
                          <Option
                            key={questionType.id}
                            value={questionType.value}
                            disabled={questionConfigs.disabledQuestionTypes.includes(questionType.value)}
                          >
                            {questionType.text}
                          </Option>
                        );
                      })}
                    </Select>
                  </Container>
                  <Container mb={5}>
                    <Input
                      id={questionID}
                      className="sm-new-survey__question-title sm-new-survey__input"
                      stretched
                      size="sm"
                      label="Question"
                      value={question.question}
                      placeholder="Enter question"
                      onChange={event => handleQuestionChange(question, event)}
                      disabled={readOnlySurvey}
                    />
                  </Container>
                  {MULTI_CHOICE_QUESTIONS.includes(question.questionType) && (
                    <MultipleChoice
                      key={question.id}
                      question={question}
                      surveyType={surveyType}
                      updateQuestion={updateQuestion}
                      maxAnsChoices={questionConfigs.maxAnsChoices}
                      readOnlySurvey={readOnlySurvey}
                    />
                  )}
                  {question.questionType === SINGLE_TEXTBOX && (
                    <Container>
                      <div className="sm-new-survey__open-ended-mock" />
                    </Container>
                  )}
                  {question.questionType === COMMENT_BOX && (
                    <Container>
                      <div className="sm-new-survey__open-ended-mock sm-new-survey__open-ended-mock__multiline" />
                    </Container>
                  )}
                  {question.questionType === STAR_RATING && (
                    <Container>
                      <div className="sm-new-survey__star-rating-mock">
                        <span className="sm-new-survey__star-rating-mock-icon">
                          <IconStarFilled />
                        </span>
                        <span className="sm-new-survey__star-rating-mock-icon">
                          <IconStarFilled />
                        </span>
                        <span className="sm-new-survey__star-rating-mock-icon">
                          <IconStarFilled />
                        </span>
                        <span className="sm-new-survey__star-rating-mock-icon">
                          <IconStarFilled />
                        </span>
                        <span className="sm-new-survey__star-rating-mock-icon">
                          <IconStarFilled />
                        </span>
                      </div>
                    </Container>
                  )}
                  {question.questionType === SLIDER && (
                    <Container mb={5}>
                      <Grid noBleeds className="sm-new-survey__slider">
                        <Row>
                          <Col>
                            <Input
                              id={`question-${question.id}-min`}
                              label="Min"
                              size="sm"
                              stretched
                              onChange={event => {
                                handleMinChange(question, event);
                              }}
                              value={question.min}
                              disabled={readOnlySurvey}
                            />
                          </Col>
                          <Col>
                            <Input
                              id={`question-${question.id}-max`}
                              label="Max"
                              size="sm"
                              stretched
                              onChange={event => {
                                handleMaxChange(question, event);
                              }}
                              value={question.max}
                              disabled={readOnlySurvey}
                            />
                          </Col>
                        </Row>
                      </Grid>
                      <Container mt={5}>
                        <div className="sm-new-survey__slider-mock" />
                      </Container>
                    </Container>
                  )}
                  {MAX_QUESTIONS !== 1 && (
                    <Container my={5}>
                      <Switch
                        disabled={readOnlySurvey}
                        checked={question.required}
                        onChange={event => handleRequiredChange(question, event)}
                      />
                      <span className="sm-new-survey__switch-label">Required</span>
                    </Container>
                  )}
                </Container>
              );
            })}
          </Col>
        </Row>
        <Row className="sm-new-survey__bottom">
          <Col>
            {MAX_QUESTIONS !== 1 && questions.length < MAX_QUESTIONS && !readOnlySurvey && (
              <Container flex flexJustify="start" my={6}>
                <Button className="sm-new-survey__add-question" variant="ghost" onClick={() => addQuestion()}>
                  + Add question
                </Button>
              </Container>
            )}
          </Col>
          <Col>
            <Toast onClose={onCloseToast} dismissible show={showToast}>
              <ToastTitle>
                Error creating {surveyType}. {errorText}
              </ToastTitle>
            </Toast>

            <Container flex flexJustify="end" my={6}>
              {!hideCancelSurveyButton && (
                <Button
                  className="wds-m-r-2 sm-new-survey__cancel-button"
                  onClick={() => handleCancel(ACTION_TYPES.CANCEL)}
                >
                  Cancel
                </Button>
              )}
              {!hideCreateSurveyButton && (
                <>
                  {secondaryCtaCopy && (
                    <Button
                      className="sm-new-survey__create-button wds-m-r-2"
                      onClick={() => handleSubmit({ overrideAction: 'preview' })}
                      disabled={isSubmitDisabled()}
                    >
                      {secondaryCtaCopy}
                    </Button>
                  )}
                  <Button
                    className="sm-new-survey__create-button"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitDisabled()}
                  >
                    {primaryCtaCopy}
                    {primaryCtaIcon}
                  </Button>
                </>
              )}
            </Container>
          </Col>
        </Row>
      </Grid>
    </Container>
  );
};

NewSurvey.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      surveyTypeSelected: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default NewSurvey;
